import React from "react";
import { useDispatch, useSelector } from "react-redux";
import ShoutOn from "graphics/icons/MediaControls/Shout/ShoutOn";
import ShoutOff from "graphics/icons/MediaControls/Shout/ShoutOff";

import {
  selectMeCamera,
  selectMeMic,
  selectMeSharing,
} from "slices/spaceMyMediaSlice";
import {
  turnOffCamera,
  turnOffMic,
  turnOnCamera,
  turnOnMic,
  turnOnScreen,
} from "slices/mediaServerSlice";
import {
  turnOffShout,
  turnOnShout,
  turnOnPinnedShare,
  turnOffPinnedShare,
  turnOffShare,
  turnOnBoard,
  // turnOnBoard,
} from "slices/spaceSlice";
import {
  selectMeMuted,
  selectMePinnedShare,
  selectMeShout,
  selectMeShoutable,
  setAdminControlsActive,
  selectMeAdmin,
  setSpaceSidebar,
  SPACE_PEOPLE,
  selectMeFocus,
} from "slices/spaceMediaSlice";
import MicOn from "graphics/icons/MediaControls/Mic/MicOn";
import MicOff from "graphics/icons/MediaControls/Mic/MicOff";
import ShareOn from "graphics/icons/MediaControls/Share/ShareOn";
import ShareOff from "graphics/icons/MediaControls/Share/ShareOff";
import CamOff from "graphics/icons/MediaControls/Cam/CamOff";
import CamOn from "graphics/icons/MediaControls/Cam/CamOn";
import { setDeviceSettingsModal } from "slices/extraOptionsSlice";
import HelperTip from "components/HOCS/HelperTip";
import HlpShareDevices from "components/HelpSlides/Spaces/HlpShareDevices";
import { FM_SHAREDEVICES, FM_SHOUT } from "helpers/freshman";
import HlpShout from "components/HelpSlides/Spaces/HlpShout";
import RemoteMediaOptions from "graphics/icons/MediaControls/RemoteMediaOptions";
import EfsTippy from "components/HOCS/EfsTippy";
import MoreOptionsTippy from "components/HOCS/MoreOptionsTippy";
import ReactSwitch from "react-switch";
import { loadAudioSounds } from "helpers/sounds";
import {
  selectCurrentMicTrackId,
  selectCurrentCameraTrackId,
} from "slices/devicesSlice";

const DeviceButtons = () => {
  const dispatch = useDispatch();

  const meAdmin = useSelector(selectMeAdmin);
  const meShout = useSelector(selectMeShout);
  const meFocus = useSelector(selectMeFocus);
  const mePinnedShare = useSelector(selectMePinnedShare);
  const meShoutable = useSelector(selectMeShoutable);
  const meMic = useSelector(selectMeMic);
  const meMicActive = useSelector(selectCurrentMicTrackId);
  const meMuted = useSelector(selectMeMuted);
  const meSharing = useSelector(selectMeSharing);
  const meCamera = useSelector(selectMeCamera);
  const meCameraActive = useSelector(selectCurrentCameraTrackId);

  let moreOptions = [
    {
      text: "Device Settings",
      action: () => {
        loadAudioSounds();
        dispatch(setDeviceSettingsModal(true));
      },
    },
  ];
  let shareOptions = [];
  if (meShoutable) {
    shareOptions.push({
      text: (
        <label className="context-option-toggle-row">
          <span>share nearby</span>
          <ReactSwitch
            height={25}
            handleDiameter={21}
            onChange={() => {
              if (!mePinnedShare) turnOnPinnedShare();
              else turnOffPinnedShare();
            }}
            checked={mePinnedShare}
            checkedIcon={false}
            uncheckedIcon={false}
          />
          <span>pin for everyone</span>
        </label>
      ),
      noHover: true,
      stayOpen: true,
    });
  }
  if (meSharing !== "board")
    shareOptions = shareOptions.concat([
      {
        text: meSharing === "screen" ? "Stop Screenshare" : "Share Screen",
        action: meSharing === "screen" ? turnOffShare : turnOnScreen,
        bigText: meSharing === "screen",
      },
    ]);

  if (meSharing !== "screen")
    shareOptions = shareOptions.concat([
      {
        text: meSharing === "board" ? "Stop Whiteboard" : "Start Whiteboard",
        action: meSharing === "board" ? turnOffShare : turnOnBoard,
        bigText: meSharing === "board",
      },
    ]);

  if (meAdmin) {
    moreOptions.push(
      {
        text: `Admin Controls`,
        action: () => {
          dispatch(setSpaceSidebar(SPACE_PEOPLE));
          dispatch(setAdminControlsActive(true));
        },
      }
      // TODO
      // Recording
    );
  }

  return (
    <HelperTip
      component={<HlpShareDevices />}
      code={FM_SHAREDEVICES}
      placement="top"
      style={{ width: "245px" }}
      offset={[0, 30]}
      arrowWhite
      lazy
    >
      <div className={`device-buttons ${meFocus ? "focusing" : ""}`}>
        <HelperTip
          component={<HlpShout />}
          placement="top"
          code={FM_SHOUT}
          style={{ width: "450px" }}
          offset={[0, 20]}
          arrowWhite
          lazy
        >
          <EfsTippy
            content={`${meShout ? "Turn Off Podium" : "Turn On Podium"}`}
            placement="top"
            className="small-Tippy grey-Tippy"
            delay={[350, 0]}
            offset={[0, 15]}
          >
            <button
              onClick={() => {
                if (meShout) turnOffShout();
                else turnOnShout();
              }}
              disabled={!meShoutable}
              // className={`${meShout ? "shouting" : ""}`}
            >
              {meShout ? <ShoutOn /> : <ShoutOff />}
              <span className="device-button-text">Podium</span>
            </button>
          </EfsTippy>
        </HelperTip>
        <EfsTippy
          content={`${meMic ? "Turn Off Mic" : "Turn On Mic"}`}
          placement="top"
          className="small-Tippy grey-Tippy"
          delay={[350, 0]}
          offset={[0, 15]}
        >
          <button
            onClick={() => {
              if (meMic) turnOffMic();
              else turnOnMic();
            }}
            disabled={meMuted}
            className={`${meMic && !meMicActive ? "faded" : ""} ${
              meShout ? "shouting" : ""
            }`}
          >
            {meMic ? <MicOn /> : <MicOff />}
            <span className="device-button-text">Mic</span>
          </button>
        </EfsTippy>
        {/* <EfsTippy
          content={`${meSharing ? "Stop Sharing" : "Share Screen"}`}
          placement="top"
          className="small-Tippy grey-Tippy"
          delay={[350, 0]}
          offset={[0, 15]}
        > */}
        <MoreOptionsTippy
          options={shareOptions}
          placement="top"
          offset={[0, 10]}
          delay={[0, 0]}
          customStyle={{ width: "193px", textAlign: "center" }}
          className={`content-only-Tippy share-Tippy ${
            mePinnedShare ? "pinned-share-active" : ""
          } pinned-sharer`}
          trigger="mouseenter focus"
          hideOnClick={false}
        >
          <button className={`${meShout || mePinnedShare ? "shouting" : ""}`}>
            {meSharing ? (
              // mePinnedShare ? (
              //   <PinnedShare />
              // ) : (
              <ShareOn />
            ) : (
              // )
              // )
              <ShareOff />
            )}
            <span className="device-button-text">Share</span>
          </button>
        </MoreOptionsTippy>
        {/* </EfsTippy> */}
        <EfsTippy
          content={`${meCamera ? "Turn Off Camera" : "Turn On Camera"}`}
          placement="top"
          className="small-Tippy grey-Tippy"
          delay={[350, 0]}
          offset={[0, 15]}
        >
          <button
            onClick={() => {
              if (meCamera) turnOffCamera();
              else turnOnCamera();
            }}
            className={`${meCamera && !meCameraActive ? "faded" : ""} ${
              meShout ? "shouting" : ""
            }`}
          >
            {meCamera ? <CamOn /> : <CamOff />}
            <span className="device-button-text">Camera</span>
          </button>
        </EfsTippy>
        <EfsTippy
          content={`More Options`}
          placement="top"
          className="small-Tippy grey-Tippy"
          delay={[350, 0]}
          offset={[0, 15]}
        >
          <MoreOptionsTippy
            options={moreOptions}
            placement="top"
            offset={[0, 20]}
            customStyle={{ width: "193px" }}
          >
            <button>
              <RemoteMediaOptions />
              {<span className="device-button-text">More</span>}
            </button>
          </MoreOptionsTippy>
        </EfsTippy>
      </div>
    </HelperTip>
  );
};
export default DeviceButtons;
